<template>
  <article class="tool-page">
    <nav class="tool-page__breadcrumbs" aria-label="Breadcrumb">
      <router-link to="/console/tools">All tools</router-link>
      <span class="tool-page__breadcrumb-separator">/</span>
      <span>Knowledge base</span>
    </nav>

    <header class="tool-page__header">
      <h1>Knowledge base</h1>
      <p class="tool-page__lead">
        A retrieval-augmented library of curated articles, learnings, and research notes.
        Hit the search endpoint, get a synthesized answer plus the underlying sources, and feed
        both into your agent.
      </p>
    </header>

    <p class="tool-page__callout">
      <strong>MCP server coming soon.</strong> A <code>search_kb</code> tool that drops into
      Claude Code and other MCP-aware assistants is on the roadmap. Until it ships, the
      curl-and-paste patterns below give agents the same capability through direct HTTP.
    </p>

    <section class="tool-page__section">
      <h2>Quick start</h2>
      <p>Search the knowledge base &mdash; one POST, synthesized answer back:</p>
      <CodeBlock language="bash" :code="quickStartCurl" />
      <p>
        The response includes a synthesized <code>answer</code> (Bedrock Sonnet over the top
        reranked chunks) plus a <code>sources</code> array of <code>articleId</code>,
        <code>title</code>, <code>section</code>, and <code>relevance</code> score. Latency is
        ~2&ndash;5s because each request runs a full RAG pipeline.
      </p>
    </section>

    <section class="tool-page__section">
      <h2>Paste this into your agent</h2>
      <p>
        Drop the block below into your agent's chat window (or system prompt). The agent will
        know how to search the knowledge base, follow each cited source, and surface citations
        the user can verify.
      </p>
      <p class="tool-page__note">
        <strong>Tradeoff:</strong> the pasted instructions, every synthesized answer, and every
        source the agent fetches sit in conversation context and get re-billed every turn.
        Once the MCP server ships (see the callout above) the tool surface will move outside
        the conversation and only the actual call and response will cost tokens.
      </p>
      <CodeBlock language="markdown" :code="agentPrompt" />
    </section>

    <section class="tool-page__section">
      <h2>Recent articles</h2>
      <p>
        A sample of what's indexed today &mdash; fetched live from
        <code>{{ articlesUrl }}</code>.
      </p>

      <div v-if="articlesState === 'loading'" class="tool-page__placeholder">
        Loading articles&hellip;
      </div>
      <div v-else-if="articlesState === 'error'" class="alert alert--error">
        Could not fetch articles from <code>{{ articlesUrl }}</code>: {{ articlesError }}
      </div>
      <table v-else class="tool-page__endpoints">
        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Author</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="article in articlesList" :key="article.articleId">
            <td>{{ article.title }}</td>
            <td>{{ article.author || '—' }}</td>
            <td><code>{{ article.status }}</code></td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="tool-page__section">
      <h2>Endpoints</h2>
      <table class="tool-page__endpoints">
        <thead>
          <tr>
            <th scope="col">Method &amp; path</th>
            <th scope="col">Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>POST /articles/search</code></td>
            <td>RAG search; body <code>{"query": "&lt;text&gt;"}</code>. Returns synthesized answer + sources.</td>
          </tr>
          <tr>
            <td><code>GET /articles</code></td>
            <td>List indexed articles (filter with <code>?status=indexed</code>, etc.)</td>
          </tr>
          <tr>
            <td><code>GET /articles/{id}</code></td>
            <td>Article metadata plus a presigned URL for the full content body</td>
          </tr>
          <tr>
            <td><code>POST /articles/ingest</code></td>
            <td>Submit a URL for ingestion; returns <code>202</code> while the pipeline runs</td>
          </tr>
          <tr>
            <td><code>GET /health</code></td>
            <td>Dependency readiness (S3 Vectors, Bedrock, DynamoDB)</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="tool-page__section">
      <h2>Search response shape</h2>
      <p>
        Successful synthesis is <code>kind: "synthesis"</code>; an empty index returns
        <code>kind: "empty"</code> with no answer. Errors return <code>kind: "error"</code> plus a
        structured <code>error</code> object naming the field and message.
      </p>
      <CodeBlock language="json" :code="searchResponseExample" />
    </section>

    <section class="tool-page__section">
      <h2>Integration patterns</h2>

      <h3>Pattern 1 &mdash; Manual paste</h3>
      <p>Run the search and paste both the answer and the sources into your agent's context:</p>
      <CodeBlock language="bash" :code="pattern1Bash" />
      <p>
        Then drop the response into your chat window. The <code>sources</code> array carries
        <code>articleId</code>s so the agent can cite them back, and you can pull the full body
        for any of them via <code>GET /articles/{id}</code>.
      </p>

      <h3>Pattern 2 &mdash; Agent-driven search</h3>
      <p>
        Add this to your agent's system prompt so it can search whenever a question would benefit
        from the library:
      </p>
      <CodeBlock language="markdown" :code="pattern2SystemPrompt" />

      <h3>Pattern 3 &mdash; Programmatic RAG augmentation</h3>
      <p>
        Use the synthesized answer as inline context for a larger model task &mdash; for example,
        as part of a Claude system prompt that already has a JSON schema for the final response.
      </p>
      <CodeBlock language="typescript" :code="pattern3Code" />
    </section>
  </article>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import CodeBlock from '@/components/CodeBlock.vue'

const baseUrl = 'https://kb.j2clark.info'
const articlesUrl = `${baseUrl}/articles`

const articlesList = ref([])
const articlesState = ref('loading')
const articlesError = ref('')

const quickStartCurl = `curl -X POST ${baseUrl}/articles/search \\
  -H 'Content-Type: application/json' \\
  -d '{"query": "prompt caching strategies"}'`

const searchResponseExample = `{
  "status": "ok",
  "kind": "synthesis",
  "answer": "Prompt caching stores frequently used context to eliminate redundant token processing, reducing inference cost by up to 75% for repetitive prompt patterns.",
  "sources": [
    {
      "articleId": "2e3471c6-...",
      "title": "The FinOps of GenAI",
      "section": "Key Strategies for GenAI FinOps",
      "relevance": 0.9
    }
  ],
  "metadata": {
    "latencyMs": 2500,
    "candidatesRetrieved": 20,
    "chunksAfterRerank": 5
  }
}`

const pattern1Bash = `curl -sX POST ${baseUrl}/articles/search \\
  -H 'Content-Type: application/json' \\
  -d '{"query": "system prompt optimization"}' > kb-answer.json`

const agentPrompt = `You have access to a curated knowledge base at ${baseUrl}.

When the user asks a question that might be answered by published research,
patterns, or learnings, search the knowledge base:

  POST ${baseUrl}/articles/search
  Content-Type: application/json
  Body: {"query": "<the user's question or keywords>"}

The response includes:
  - "answer":  a synthesized answer over the top-reranked chunks
  - "sources": each with articleId, title, section, and a relevance score

If you need the full text of a cited source, fetch it:

  GET ${baseUrl}/articles/<articleId>

Follow the "contentUrl" field in the response (presigned S3) for the body.

Always cite the source title(s) so the user can verify the answer.`

const pattern2SystemPrompt = agentPrompt

const pattern3Code = `// Use the KB synthesis as inline context for a downstream model call.
const search = await fetch('${baseUrl}/articles/search', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ query: userQuestion }),
}).then(r => r.json());

const context = [
  search.answer,
  ...search.sources.map(s => \`- \${s.title}\${s.section ? ' / ' + s.section : ''}\`),
].join('\\n');

const result = await client.messages.create({
  model: 'claude-opus-4-7',
  max_tokens: 2048,
  system: \`You answer questions using the supplied knowledge-base context.
Cite sources by title.

Context:
\${context}\`,
  messages: [{ role: 'user', content: userQuestion }],
});`

onMounted(async () => {
  try {
    const response = await fetch(articlesUrl, { headers: { Accept: 'application/json' } })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const data = await response.json()
    const articles = Array.isArray(data.articles) ? data.articles : []
    articlesList.value = articles.slice(0, 8).map((article) => ({
      articleId: article.articleId,
      title: article.title,
      author: article.author,
      status: article.status,
    }))
    articlesState.value = 'ready'
  } catch (err) {
    articlesError.value = err instanceof Error ? err.message : String(err)
    articlesState.value = 'error'
  }
})
</script>

<style src="@/styles/console.css"></style>
<style src="@/styles/tool-page.css"></style>
